/**
 * useUserDetail — composes the existing `/salescoach/api/user/:username/*` hooks
 * into a single hook that the UserDetail modal consumes (v1.4 H2).
 *
 * Rather than introduce a new backend endpoint, this hook layers the four
 * slices the modal actually renders on top of the drill-down hooks:
 *
 *   - `profile`  ← useUserProfile               (Overview tab)
 *   - `kpis`     ← useUserKpis                  (Overview tab)
 *   - `activity` ← useUserCharts (.requests)    (Activity tab — requests trend)
 *   - `tools`    ← useUserTopTools              (Tools/topics tab)
 *
 * The Feedback tab is no longer hydrated here. Phase 8 Plan 04 migrated it
 * to `useTracesList({...filters, users:[username]}, {hasFeedback:'rated'})`
 * — the modal calls that hook directly. Slimming this composite removes a
 * second network round-trip for the deleted `/feedback-timeline` endpoint.
 *
 * Every section exposes `{ data, isLoading, isError, refetch }` so the
 * modal can render per-section empty / loading / error states without
 * conflating them. The global FilterBar's filters drive every time-scoped
 * slice so the modal honours whatever the user already set on the page;
 * profile stays lifetime-scoped per the Plan 04-04 truth (profile is
 * time-independent).
 *
 * Pass `userId = null | undefined` to no-op every inner query (the same
 * `enabled` gate the drill-down hooks already implement). That way the
 * modal can mount unconditionally and the queries only fire once a user
 * id is selected.
 */

import { useUrlFilters } from '@/hooks/use-url-filters';
import {
  useUserCharts,
  useUserKpis,
  useUserProfile,
  useUserTopTools,
} from '@/hooks/use-user-drilldown';

export interface UseUserDetailResult {
  profile: ReturnType<typeof useUserProfile>;
  kpis: ReturnType<typeof useUserKpis>;
  activity: ReturnType<typeof useUserCharts>;
  tools: ReturnType<typeof useUserTopTools>;
}

/**
 * Hydrate the four non-feedback sections of the UserDetail modal in one
 * call. `userId` can be null / undefined when the modal is closed — every
 * inner query keys on the userId and gates its `enabled` flag on
 * truthiness, so no network traffic happens until a user is actually
 * opened. The Feedback tab fetches its own slice via `useTracesList`.
 */
export function useUserDetail(
  userId: string | null | undefined,
): UseUserDetailResult {
  const { filters } = useUrlFilters();
  const username = userId ?? undefined;

  const profile = useUserProfile(username);
  const kpis = useUserKpis(username, filters);
  const activity = useUserCharts(username, filters, filters.granularity);
  // `useUserTopTools` gracefully returns an empty array when the user has
  // no tool-calls in the window, so the Tools/topics tab can show an
  // empty-state card instead of an error.
  const tools = useUserTopTools(username, filters, { limit: 10 });

  return { profile, kpis, activity, tools };
}
